export const ROUTE_STYLE_STORAGE_KEY = 'hiiker_route_style_prefs';

export interface RouteTypePrefs {
  color: string;
  thickness: number; // multiplier: 0.5 | 1 | 2 | 3
  opacity: number;   // 10-100
}

export interface RouteStyleSettings {
  main: RouteTypePrefs;
  alt: RouteTypePrefs;
  spur: RouteTypePrefs;
  recording: RouteTypePrefs;
  hideMileMarkers: boolean;
}

export const DEFAULT_ROUTE_STYLE_SETTINGS: RouteStyleSettings = {
  main:      { color: '#DB2B35', thickness: 1, opacity: 100 },
  alt:       { color: '#00FFFF', thickness: 1, opacity: 100 },
  spur:      { color: '#FF00FF', thickness: 1, opacity: 100 },
  recording: { color: '#9E00FF', thickness: 1, opacity: 100 },
  hideMileMarkers: false,
};

export function getRouteStyleSettings(): RouteStyleSettings {
  try {
    const raw = localStorage.getItem(ROUTE_STYLE_STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      return {
        main:      { ...DEFAULT_ROUTE_STYLE_SETTINGS.main,      ...(p.main      || {}) },
        alt:       { ...DEFAULT_ROUTE_STYLE_SETTINGS.alt,       ...(p.alt       || {}) },
        spur:      { ...DEFAULT_ROUTE_STYLE_SETTINGS.spur,      ...(p.spur      || {}) },
        recording: { ...DEFAULT_ROUTE_STYLE_SETTINGS.recording, ...(p.recording || {}) },
        hideMileMarkers: p.hideMileMarkers ?? DEFAULT_ROUTE_STYLE_SETTINGS.hideMileMarkers,
      };
    }
  } catch {}
  return {
    main:      { ...DEFAULT_ROUTE_STYLE_SETTINGS.main      },
    alt:       { ...DEFAULT_ROUTE_STYLE_SETTINGS.alt       },
    spur:      { ...DEFAULT_ROUTE_STYLE_SETTINGS.spur      },
    recording: { ...DEFAULT_ROUTE_STYLE_SETTINGS.recording },
    hideMileMarkers: DEFAULT_ROUTE_STYLE_SETTINGS.hideMileMarkers,
  };
}

/**
 * Muted variant of a route color for de-emphasised (inactive) routes:
 * blends toward mid-gray, keeping the hue readable — a dusty version of the
 * color rather than a transparent one, so inactive routes stay followable.
 */
export function muteHex(hex: string, amount = 0.55): string {
  const clean = hex.replace('#', '').padEnd(6, '0');
  const mix = (channel: number) => Math.round(channel * (1 - amount) + 128 * amount);
  const r = mix(parseInt(clean.slice(0, 2), 16));
  const g = mix(parseInt(clean.slice(2, 4), 16));
  const b = mix(parseInt(clean.slice(4, 6), 16));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function darkenHex(hex: string, factor = 0.45): string {
  const clean = hex.replace('#', '').padEnd(6, '0');
  const r = Math.round(parseInt(clean.slice(0, 2), 16) * factor);
  const g = Math.round(parseInt(clean.slice(2, 4), 16) * factor);
  const b = Math.round(parseInt(clean.slice(4, 6), 16) * factor);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Builds Mapbox paint-property expressions from the current custom route style settings.
 * transport routes inherit main (route) color/thickness.
 */
export function buildRouteStyleExpressions(s: RouteStyleSettings) {
  const colorExpr: any[] = [
    'case',
    ['==', ['get', 'routeStyle'], 'route'],     s.main.color,
    ['==', ['get', 'routeStyle'], 'alt'],        s.alt.color,
    ['==', ['get', 'routeStyle'], 'spur'],       s.spur.color,
    ['==', ['get', 'routeStyle'], 'transport'],  s.main.color,
    s.main.color,
  ];

  // Muted (inactive-route) colors — dusty versions of each style color
  const mutedColorExpr: any[] = [
    'case',
    ['==', ['get', 'routeStyle'], 'route'],     muteHex(s.main.color),
    ['==', ['get', 'routeStyle'], 'alt'],        muteHex(s.alt.color),
    ['==', ['get', 'routeStyle'], 'spur'],       muteHex(s.spur.color),
    ['==', ['get', 'routeStyle'], 'transport'],  muteHex(s.main.color),
    muteHex(s.main.color),
  ];

  const mutedBorderColorExpr: any[] = [
    'case',
    ['==', ['get', 'routeStyle'], 'route'],     darkenHex(muteHex(s.main.color)),
    ['==', ['get', 'routeStyle'], 'alt'],        darkenHex(muteHex(s.alt.color)),
    ['==', ['get', 'routeStyle'], 'spur'],       darkenHex(muteHex(s.spur.color)),
    ['==', ['get', 'routeStyle'], 'transport'],  darkenHex(muteHex(s.main.color)),
    darkenHex(muteHex(s.main.color)),
  ];

  const borderColorExpr: any[] = [
    'case',
    ['==', ['get', 'routeStyle'], 'route'],     darkenHex(s.main.color),
    ['==', ['get', 'routeStyle'], 'alt'],        darkenHex(s.alt.color),
    ['==', ['get', 'routeStyle'], 'spur'],       darkenHex(s.spur.color),
    ['==', ['get', 'routeStyle'], 'transport'],  darkenHex(s.main.color),
    darkenHex(s.main.color),
  ];

  // Active line widths (base 5) scaled by per-type thickness
  const activeLineWidthExpr: any[] = [
    'case',
    ['==', ['get', 'routeStyle'], 'route'],     5 * s.main.thickness,
    ['==', ['get', 'routeStyle'], 'alt'],        5 * s.alt.thickness,
    ['==', ['get', 'routeStyle'], 'spur'],       5 * s.spur.thickness,
    ['==', ['get', 'routeStyle'], 'transport'],  5 * s.main.thickness,
    5,
  ];

  // Inactive line widths (base 3)
  const inactiveLineWidthExpr: any[] = [
    'case',
    ['==', ['get', 'routeStyle'], 'route'],     3 * s.main.thickness,
    ['==', ['get', 'routeStyle'], 'alt'],        3 * s.alt.thickness,
    ['==', ['get', 'routeStyle'], 'spur'],       3 * s.spur.thickness,
    ['==', ['get', 'routeStyle'], 'transport'],  3 * s.main.thickness,
    3,
  ];

  // Active border widths (base 9)
  const activeBorderWidthExpr: any[] = [
    'case',
    ['==', ['get', 'routeStyle'], 'route'],     9 * s.main.thickness,
    ['==', ['get', 'routeStyle'], 'alt'],        9 * s.alt.thickness,
    ['==', ['get', 'routeStyle'], 'spur'],       9 * s.spur.thickness,
    ['==', ['get', 'routeStyle'], 'transport'],  9 * s.main.thickness,
    9,
  ];

  // Inactive border widths (base 7)
  const inactiveBorderWidthExpr: any[] = [
    'case',
    ['==', ['get', 'routeStyle'], 'route'],     7 * s.main.thickness,
    ['==', ['get', 'routeStyle'], 'alt'],        7 * s.alt.thickness,
    ['==', ['get', 'routeStyle'], 'spur'],       7 * s.spur.thickness,
    ['==', ['get', 'routeStyle'], 'transport'],  7 * s.main.thickness,
    7,
  ];

  // Active line opacity (custom; transport always 1 since it has no separate border)
  const activeLineOpacityExpr: any[] = [
    'case',
    // Still waiting for GraphHopper (see plan_trail/utils/pendingSnap.ts): faint until it lands.
    ['==', ['get', 'pendingSnap'], true],        0.5,
    ['==', ['get', 'routeStyle'], 'route'],     s.main.opacity / 100,
    ['==', ['get', 'routeStyle'], 'alt'],        s.alt.opacity / 100,
    ['==', ['get', 'routeStyle'], 'spur'],       s.spur.opacity / 100,
    ['==', ['get', 'routeStyle'], 'transport'],  s.main.opacity / 100,
    1.0,
  ];

  // Active border opacity (same per-type values; transport border is hidden)
  const activeBorderOpacityExpr: any[] = [
    'case',
    ['==', ['get', 'routeStyle'], 'transport'], 0,
    ['==', ['get', 'pendingSnap'], true],        0,
    ['==', ['get', 'routeStyle'], 'route'],     s.main.opacity / 100,
    ['==', ['get', 'routeStyle'], 'alt'],        s.alt.opacity / 100,
    ['==', ['get', 'routeStyle'], 'spur'],       s.spur.opacity / 100,
    1.0,
  ];

  // Inactive layers are always visually dimmed regardless of custom opacity
  const inactiveLineOpacity = 0.4;
  const inactiveBorderOpacityExpr: any[] = [
    'case',
    ['==', ['get', 'routeStyle'], 'transport'], 0,
    0.4,
  ];

  return {
    colorExpr,
    borderColorExpr,
    mutedColorExpr,
    mutedBorderColorExpr,
    activeLineWidthExpr,
    inactiveLineWidthExpr,
    activeBorderWidthExpr,
    inactiveBorderWidthExpr,
    activeLineOpacityExpr,
    activeBorderOpacityExpr,
    inactiveLineOpacity,
    inactiveBorderOpacityExpr,
  };
}
